import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface TopicWeight {
  topic: string;
  weight: number; // -1.0 .. 1.0 (or 0..1 depending on UI)
}

export function useTopicWeights() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['user-topic-weights', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return [] as TopicWeight[];
      const { data, error } = await supabase
        .from('user_topic_weights')
        .select('topic, weight')
        .eq('user_id', user.id)
        .order('weight', { ascending: false });
      if (error) throw error;
      return (data || []) as TopicWeight[];
    },
    staleTime: 60_000,
  });

  const upsert = useMutation({
    mutationFn: async (items: TopicWeight[] | TopicWeight) => {
      if (!user?.id) throw new Error('Authentication required');
      const list = Array.isArray(items) ? items : [items];
      const rows = list.map((i) => ({ user_id: user!.id, topic: i.topic, weight: i.weight }));
      const { error } = await supabase.from('user_topic_weights').upsert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-topic-weights'] });
    },
  });

  return {
    weights: query.data || [],
    isLoading: query.isLoading,
    error: query.error as any,
    upsertTopicWeights: upsert.mutate,
    isSaving: upsert.isPending,
  };
}
